/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretDown } from "@fortawesome/free-solid-svg-icons";
import { MdLanguage } from "react-icons/md";
import { LANGUAGES } from "./constants";

interface LanguageSwitcherProps {
  selectedLanguage: string;
  onLanguageChange: (lang: string) => void;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  selectedLanguage,
  onLanguageChange,
}) => {
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const { t } = useTranslation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = useCallback(
    (langId: string) => {
      setDropdownVisible(false);
      onLanguageChange(langId);
      triggerRef.current?.focus();
    },
    [onLanguageChange],
  );

  const handleDropdownKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      setDropdownVisible(false);
      triggerRef.current?.focus();
    } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const options =
        dropdownRef.current?.querySelectorAll<HTMLButtonElement>(
          '[role="option"]',
        );
      if (!options?.length) return;
      const currentIndex = Array.from(options).findIndex(
        (el) => el === document.activeElement,
      );
      const nextIndex =
        e.key === "ArrowDown"
          ? Math.min(currentIndex + 1, options.length - 1)
          : Math.max(currentIndex - 1, 0);
      options[nextIndex]?.focus();
    }
  }, []);

  return (
    <div className="select-language" ref={dropdownRef}>
      {/* WCAG SC 4.1.2: the button needs an accessible name that conveys
          its purpose. aria-label="Select Language" (from existing locale key)
          gives screen readers a clear label independent of the visible text. */}
      <button
        ref={triggerRef}
        onClick={() => setDropdownVisible((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setDropdownVisible((prev) => !prev);
          } else if (e.key === "Escape" && dropdownVisible) {
            e.preventDefault();
            setDropdownVisible(false);
          }
        }}
        className={dropdownVisible ? "lang-box-clicked" : "lang-box"}
        aria-haspopup="listbox"
        aria-expanded={dropdownVisible}
        aria-label={t("accessibility.select_language")}
        type="button"
      >
        <MdLanguage className="language-icon" />
        <p>
          {LANGUAGES.find((lang) => lang.id === selectedLanguage)?.name ??
            "Select Language"}
        </p>
        <FontAwesomeIcon
          icon={faCaretDown}
          className="language-dropdown"
          style={dropdownVisible ? { rotate: "180deg" } : { rotate: "unset" }}
        />
      </button>
      {dropdownVisible && (
        <div
          className="dropdown"
          role="listbox"
          tabIndex={-1}
          aria-label={t("accessibility.select_language")}
          onKeyDown={handleDropdownKeyDown}
        >
          {LANGUAGES.map((lang) => (
            <button
              style={
                selectedLanguage === lang.id
                  ? { backgroundColor: "#ebebeb" }
                  : { backgroundColor: "white" }
              }
              key={lang.id}
              onClick={() => handleSelect(lang.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleSelect(lang.id);
                }
              }}
              role="option"
              aria-selected={selectedLanguage === lang.id}
              type="button"
            >
              {lang.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
